import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Heart } from 'lucide-react'
import { trpc } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'

const SLICES = [
  { key: 'likes', label: 'Likes', color: 'hsl(0, 84%, 60%)' },
  { key: 'comments', label: 'Comments', color: 'hsl(142, 71%, 45%)' },
  { key: 'shares', label: 'Shares', color: 'hsl(262, 83%, 58%)' },
  { key: 'saves', label: 'Saves', color: 'hsl(38, 92%, 50%)' },
] as const

type SliceKey = (typeof SLICES)[number]['key']

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

export function EngagementQualityCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.engagementQuality.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Heart size={14} className="text-muted-foreground" />
          Engagement Quality
          <InfoTooltip>
            How your audience engages — saves and shares mean reference value and advocacy; comments
            mean conversation; likes mean passive approval. The mix tells you what role your content
            plays for them.
          </InfoTooltip>
          {data?.lowConfidence && data.hasEnoughData && (
            <HintLabel
              hint="Below 5 posts the composition can shift dramatically with a single new post. Treat as directional, not definitive."
              className="text-muted-foreground/70 text-[10px] font-normal">
              (low confidence)
            </HintLabel>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-4">
            <div className="bg-muted h-20 w-20 animate-pulse rounded-full" />
            <div className="flex-1 space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-muted h-3 animate-pulse rounded" />
              ))}
            </div>
          </div>
        )}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            No engagement recorded yet for posts in this filter.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className="flex items-center gap-4">
            <Donut composition={data.composition} />
            <ul className="flex-1 space-y-1.5">
              {SLICES.map(({ key, label, color }) => {
                const pct = data.composition[key as SliceKey]
                const count = data.counts[key as SliceKey]
                return (
                  <li key={key} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-foreground">{label}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-muted-foreground">{formatNumber(count)}</span>
                      <span className="text-foreground font-medium tabular-nums">
                        {(pct * 100).toFixed(0)}%
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Donut({
  composition,
}: {
  composition: { likes: number; comments: number; saves: number; shares: number }
}) {
  // SVG donut composed of stroke-dasharray segments — cheaper than pulling in a
  // chart lib for 4 slices, and theme colors stay consistent with the legend.
  const radius = 28
  const strokeWidth = 12
  const circumference = 2 * Math.PI * radius

  // Cumulative offset per slice, computed functionally so the React Compiler
  // doesn't trip on a mutable `let` inside the render body.
  const lengths = SLICES.map(({ key }) => composition[key as SliceKey] * circumference)
  const offsets = lengths.map((_, i) => lengths.slice(0, i).reduce((sum, l) => sum + l, 0))

  const segments = SLICES.map(({ key, color }, i) => (
    <circle
      key={key}
      cx="40"
      cy="40"
      r={radius}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeDasharray={`${lengths[i]} ${circumference - lengths[i]!}`}
      strokeDashoffset={-offsets[i]!}
      transform="rotate(-90 40 40)"
    />
  ))

  return (
    <svg width="80" height="80" viewBox="0 0 80 80" className="shrink-0">
      <circle cx="40" cy="40" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={1} />
      {segments}
    </svg>
  )
}
